import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import useOrdersExport from '@/hooks/useOrdersExport';
import { SectionLabel } from './ExportScopeCards';

type OrdersExportState = ReturnType<typeof useOrdersExport>;

const ALL_STATUSES = '__all';

export const PILL =
  'h-8 rounded-full border border-solid border-sd-border bg-white px-3 text-xs font-medium text-sd-foreground hover:bg-sd-muted hover:text-sd-foreground ' +
  'focus-visible:ring-sd-primary focus-visible:ring-offset-1 ' +
  'data-[state=on]:border-sd-primary data-[state=on]:bg-sd-primary/15 data-[state=on]:text-sd-foreground data-[state=on]:ring-1 data-[state=on]:ring-sd-primary';

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className='flex flex-col gap-2'>
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  );
}

interface ExportCustomFiltersProps {
  state: OrdersExportState;
  disabled: boolean;
}

/**
 * BOXY-409 — the Custom scope: status pills ("All" or any of the groups). The
 * date window is the shared Period (ExportPeriod), not repeated here.
 */
export function ExportCustomFilters({ state, disabled }: ExportCustomFiltersProps) {
  const { t } = useTranslation();

  const onStatuses = (next: string[]) => {
    const pickedAll = next.includes(ALL_STATUSES) && state.statusGroups.length > 0;
    state.setStatusGroups(pickedAll ? [] : next.filter((value) => value !== ALL_STATUSES));
  };

  return (
    <div className='flex flex-col gap-4 rounded-2xl border border-solid border-sd-border bg-sd-muted/40 p-4 duration-300 animate-in fade-in-0 slide-in-from-top-2 motion-reduce:animate-none'>
      <Field label={String(t('export_modal.status'))}>
        <ToggleGroup
          type='multiple'
          value={state.statusGroups.length ? state.statusGroups : [ALL_STATUSES]}
          onValueChange={onStatuses}
          disabled={disabled}
          aria-label={String(t('export_modal.status'))}
          className='flex-wrap justify-start gap-2'
        >
          <ToggleGroupItem value={ALL_STATUSES} className={PILL}>
            {String(t('export_modal.status_all'))}
          </ToggleGroupItem>
          {state.statusOptions.map((option) => (
            <ToggleGroupItem key={option.value} value={option.value} className={PILL}>
              {String(t(`merchant_order_status.${option.firstSlug}`))}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Field>
    </div>
  );
}
